import $ from 'jquery';
import * as R from 'ramda';
import features from 'system/features';
import radar from 'system/radar';
import { DPPEventType, ErrorType } from 'utilities/DPP/DPP.types';
import { JsonSafe } from 'utils/JsonSafe';
import { RandomHelper } from 'utils/RandomHelper';

import { Environment } from 'Environment';
import { BoundCurrentUserSelectors } from 'reactApp/modules/CurrentUserModule/CurrentUserModule.selectors';
import xPageId from 'xPageId';
import xTabId from 'xTabId';

const dppUrl = '/infra/dpp/';
const namespace = 'mcs:front';

const sendEvent = async ({ type, payload, error }: DPPEventType) => {
    if (getIsMuted()) {
        return false;
    }
    let event: any = {};
    const start = new Date().getTime();

    event = {
        email: BoundCurrentUserSelectors.getEmail(),
        pid: BoundCurrentUserSelectors.getPid(),
        location: window.location.href,
        version: process.env.COMMIT,
        xTabId,
        xPageId,
        tag: getUserTag(),
        ...payload,
    };

    if (error) {
        event = {
            ...event,
            message: getErrorMessage(error),
            status:
                payload && 'status' in payload
                    ? payload.status
                    : getErrorStatus(error),
            eventId: `${xPageId}_${xTabId}_${RandomHelper.getRandomInt(10000)}`,
        };
    }

    const data = {
        timestamp: getISOString(),
        event_type: `${namespace}:${type}`,
        event: prepareData(event),
    };

    $.ajax({
        type: 'POST',
        url: dppUrl,
        data: JsonSafe.stringify(data),
        contentType: 'application/json',
        headers: { 'Cache-Control': 'no-cache' },
    })
        .done(() => {
            const duration = new Date().getTime() - start;
            radar('dpp')
                .add('dur', duration)
                .add('dur-successful', duration)
                .add('cnt')
                .add('cnt-successful')
                .send();
        })
        .fail((error) => {
            console.error(error);
            const duration = new Date().getTime() - start;
            const status = error?.status || 0;

            const metrics = radar('dpp')
                .add('http' + status)
                .add('cnt')
                .add('cnt-failed')
                .add('dur-failed', duration)
                .add('dur', duration);

            if (status === 429) {
                metrics.add('rate-limit-cnt');
            }

            if (type === 'request' && payload && 'target' in payload) {
                metrics.add(payload.target + '-cnt');
                metrics.add(payload.target + '-dur', duration);
                metrics.kaktam('dpp', {
                    location: event.location,
                    status,
                    duration,
                    method: payload.method,
                    pid: event.pid,
                });
            }

            metrics.send();
        });
};

function getUserTag() {
    if (features.isTestUser()) {
        return 'test';
    }

    if (features.isCorpUser()) {
        return 'corp';
    }

    return 'lead';
}

function getIsMuted() {
    return (
        Environment.isPrivateBuild ||
        Environment.isAutoTesting ||
        Environment.isDevelopmentRun ||
        !features.isEnabled('dpp')
    );
}

function prepareData(data) {
    // DPP принмает только плоский объект с key-value в виде строк
    return R.pipe(
        // @ts-ignore
        R.map(String),
    )(data);
}

function getISOString() {
    const date = new Date();
    return date.toISOString();
}

function getErrorMessage(error: ErrorType) {
    if (typeof error === 'string') {
        return error;
    }

    if ('responseText' in error && error.responseText) {
        return error.responseText;
    }

    if ('message' in error && error.message) {
        return error.message;
    }

    if ('status' in error && error.status === 0) {
        return 'Не дождались выполнения запроса или запрос был прерван';
    }

    return JsonSafe.stringify(error);
}

function getErrorStatus(error: ErrorType) {
    if (typeof error === 'string') {
        return 'unknown';
    }

    if ('responseText' in error) {
        return error.status;
    }

    if ('statusCode' in error) {
        return error.statusCode;
    }

    return 'unknown';
}

export const DPP = {
    sendEvent,
    getUserTag,
};

/**
 * @overview DPP - data platform protocol
 * Система для логирования метрик, событий и ошибок.
 */
